import { Injectable, Renderer2, RendererFactory2 } from '@angular/core'; // Added Renderer2, RendererFactory2
import { BehaviorSubject, Observable } from 'rxjs';


import { AjaxService } from './ajax.service';
import { ConfigService } from './config.service';
import { TranslateService } from './translate.service';
import { ViewScopeService } from './view-scope.service';

import { SET_LANG } from './account-api.constants';

@Injectable({
	providedIn: 'root'
})
export class DirectionService {
	// Use a specific union type for better type safety
	private _direction: BehaviorSubject<'ltr' | 'rtl'>;
	public currentDirection$: Observable<'ltr' | 'rtl'>;

	private renderer: Renderer2; // Declare renderer if using Renderer2

	constructor(rendererFactory: RendererFactory2,
				private ajaxService: AjaxService, 
				private configService: ConfigService,
				private viewScopeService: ViewScopeService, 
				private translateService: TranslateService) {
		this.renderer = rendererFactory.createRenderer(null, null); // Initialize renderer

		// 1. Get saved direction from localStorage
		const savedDirection = localStorage.getItem('direction');
		let initialDirection: 'ltr' | 'rtl'; // Declare type for clarity

		// Validate saved direction, otherwise determine based on browser or default
		if (savedDirection === 'rtl' || savedDirection === 'ltr') {
			initialDirection = savedDirection;
			console.log(`DirectionService: Initializing from localStorage: ${savedDirection}`);
		} else {
			// 2. Fallback: Determine initial direction based on browser language
			const browserLang = navigator.language.split('-')[0];
			// Add more RTL language codes if needed
			if (['ar', 'he', 'fa', 'ur'].includes(browserLang)) {
				initialDirection = 'rtl';
				console.log(`DirectionService: Initializing from browser language (${browserLang}): rtl`);
			} else {
				initialDirection = 'ltr'; // Explicitly set LTR if not an identified RTL language
				console.log(`DirectionService: Initializing with default: ltr`);
			}
		}

		// Initialize BehaviorSubject with the guaranteed 'ltr' or 'rtl' string
		this._direction = new BehaviorSubject<'ltr' | 'rtl'>(initialDirection);
		this.currentDirection$ = this._direction.asObservable(); // Expose as Observable

		// Immediately apply the determined direction to the HTML document
		this.setHtmlDirAttribute(initialDirection);
	}

	/**
	 * Sets the application's text direction (LTR or RTL).
	 * Updates internal state, localStorage, and the <html> element.
	 * @param direction The desired direction ('ltr' or 'rtl').
	 */
	setDirection(direction: 'ltr' | 'rtl'): void {
		console.log(`DirectionService: Setting direction to: ${direction}`);
		this._direction.next(direction); // Update the BehaviorSubject
		localStorage.setItem('direction', direction); // Save preference

		// Dynamically set the 'dir' attribute on the <html> element
		this.setHtmlDirAttribute(direction);
	}

	public updateLanguage(lang: 'he' | 'en'): boolean {
		let toUpdate = true;

		const url = this.ajaxService.getBaseOfApi() + SET_LANG + this.viewScopeService.getCurrentOpertaingUserId() + '/' + lang.toUpperCase();
		console.log("1> " + url);
		this.ajaxService.getData(url).subscribe(
			{
				next: (res) => {
					const resultArray = res as { RESULT: string }[];
					if (resultArray && resultArray.length > 0) {
						const result = resultArray[0].RESULT;
						toUpdate = Number(result) === this.viewScopeService.getCurrentOpertaingUserId();
						this.translateService.loadDictionary().then(() => {
							location.reload();
						});
					}
				},
				error: (error) => {
					console.error('Error fetching posts:', error);
				}
			});
		return toUpdate;
	}

	/**
	 * Returns true if the current direction is RTL, false otherwise.
	 * Reads directly from the BehaviorSubject for consistency.
	 */
	isRtl(): boolean {
		const currentValue = this._direction.value;
		// console.warn(`DirectionService: INSIDE IS_RTL: ${currentValue} - ${currentValue === 'rtl'}`);
		return currentValue === 'rtl';
	}

	/**
	 * Helper to set the 'dir' attribute on the root <html> element.
	 * Uses Renderer2 for Angular-idiomatic DOM manipulation.
	 */
	private setHtmlDirAttribute(direction: 'ltr' | 'rtl'): void {
		this.renderer.setAttribute(document.documentElement, 'dir', direction);
		// Alternatively, direct DOM manipulation (works but Renderer2 is preferred in Angular)
		// document.documentElement.setAttribute('dir', direction);
	}


	/**
	 * Public getter for current direction value ('ltr' or 'rtl') for template binding.
	 */
	public get directionValue(): 'ltr' | 'rtl' {
		return this._direction.value;
	}
}